import * as React from "react";
import { useDataLoader, usePerformAction } from "react-remote-data-hooks";
import api from "../../../api";
import { LeadRequirement } from "../../../core/api/models/requirement";
import "../../../styles/full-view-popup.scss";
import "../../../styles/requirement.scss";
import "../../../styles/sales-person.scss";
import "../../../styles/tags.scss";
import { groupBy, sortBy, values } from "lodash";
import { toastr } from "react-redux-toastr";
import {
  useForm
} from "react-hook-form";
import { ContainerLoader } from "../../../components/loader";
import {
  AppPermissions, groupProjects,
  Proposal, ProposalReason,
  SendProposalStatus, useHasPermission
} from "../../../core";
import { PropertyDetails } from "../../../core/api/models/project";
import { FormattedData } from "./EditProposedCommercials";
import ProposalPreviewHeader from "./proposal-preview-header";
import ProposalApprovalModal from "./proposal-approval-modal";
import ProposalRejectModal from "./proposal-reject-modal";
import ProposalYouWillLoseChangesPopup from "./proposal-you-will-lose-changes-popup";
import ProposalPreviewThankYou from "./proposal-preview-thank-you";
import EditProposedCommercialsWrapper from "./Edit-Proposed-Commercials-Wrapper";
import { ProposalImage } from "./proposal-images";
import { ProposalProjectImages } from "./proposal-project-images";
import { ProposalRequirementBrief } from "./proposal-requirement-brief";
import { ProposalLocationMap } from "./proposal-location-map";
import { ProposalFormattedPropertiesDetails } from "./proposal-formatted-properties-details";
import { FurnishingDetailsViewOption } from "../../create-proposal/components/props";
import { ProjectImage } from "../../create-proposal/props";

// Add required image sources
const publicUrl = window.location.origin; // process.env.PUBLIC_URL;
const defaultIcon = `${publicUrl}/default-amenities.png`;
const cctvIcon = `${publicUrl}/CCTV.png`;
const waterSupplyIcon = `${publicUrl}/Water-Supply.png`;
const visitorParkingIcon = `${publicUrl}/default-amenities.png`;
const parkingIcon = `${publicUrl}/Parking.png`;
const acIcon = `${publicUrl}/Mask-group.png`;
const securityIcon = `${publicUrl}/Security.png`;

interface IPropertyDetailsPageProps {
  requirementId: string;
  requirementData?: LeadRequirement;
  createData?: any;
  selectedProperties?: PropertyDetails[];
  selectedExcludedFloorIds?: string[];
  selectedExcludedPhotoGalleryIds?: string[];
  selectedExcludedFurnishingIds?: string[];
  onClose?: () => void;
  onSend?: () => void;
  isSaving?: boolean;
  proposalId?: string;
  proposalIdForPreview?: string;
  onChangeStatus?: (status: SendProposalStatus) => void;
  reload?: () => void;
  refreshComponent?: () => void;
  onEdit?: (reqId: string) => void;
  hideEdit?: boolean;
  floorplanAndFurnishingDetails?: {
    propertyId: string;
    furnishingDetailsViewOption: string;
  }[];
  editedProposedPropertyData?: FormattedData[];
  setEditedProposedPropertyData?: React.Dispatch<React.SetStateAction<FormattedData[]>>;
  selectedProjectImages?: ProjectImage[];
  isEdited?: boolean;
}

export interface FormProps {
  reason: string;
  comment?: string;
}

const ProposalPreview: React.FunctionComponent<IPropertyDetailsPageProps> = (
  props
) => {
  const [statusModalOpen, setStatusModalOpen] = React.useState<{
    isOpen?: boolean;
    status?: SendProposalStatus;
  }>({});

  const { data: reasons } = useDataLoader<ProposalReason[]>(() =>
    api.proposal.loadAllProposalReason({ entityType: "ProposalReject" })
  );

  const { control, errors, handleSubmit } = useForm<FormProps>({
    reValidateMode: "onChange",
    mode: "onSubmit",
  });

  const [updating, setUpdating] = React.useState(false);
  const [commercialsEdited, setCommercialsEdited] = React.useState(false);
  const [showYouWillLoseChangePopup, setShowYouWillLoseChangePopup] = React.useState<{ isOpen: boolean, id: string | null, text: string, onPressYes: () => void | undefined }>({
    isOpen: false, id: null, text: '', onPressYes: () => {
      //
    }
  });
  const componentRef = React.useRef<HTMLImageElement>(null);
  const canCreateProposal = useHasPermission(AppPermissions.CreateProposal);

  const canUpdateProposal = useHasPermission(AppPermissions.UpdateProposal);
  const canApproveProposal = useHasPermission(AppPermissions.ApproveProposal);
  const canRejectProposal = useHasPermission(AppPermissions.RejectProposal);
  const canEditCommercialDetails = useHasPermission(AppPermissions.EditCommercialDetails);
  const [proposedPropertyData, setProposedPropertyData] = React.useState<any>();
  const [enableEdit, setEnableEdit] = React.useState<boolean>(false);
  const [isEditProposedCommercials, setIsEditProposedCommercials] = React.useState<{ isOpen: boolean, data: any, propertiesForProject: PropertyDetails[] }>({ isOpen: false, data: null, propertiesForProject: [] });

  const changeStatus = async (data?: any) => {
    setUpdating(true);
    try {
      await api.proposal.updateSendProposalStatus({
        proposalId: props.proposalId!,
        status: statusModalOpen.status,
        rejectReason: data?.reason || "",
        comment: data?.comment || "",
      });
      toastr.success("Success", `Proposal is ${statusModalOpen.status}`);
      props.reload?.();
      reloadProposal(proposalId);
      setStatusModalOpen({});
    } catch (error) {
      toastr.error("Error", "Failed to update Status");
    }
    setUpdating(false);
  };

  const {
    requirementId,
    requirementData,
    selectedProperties,
    proposalId,
  } = props;

  const formatSelectedProperties = (sltVal: any) => {
    return sltVal?.map((item: { propertyId: any }) => ({
      ...item,
      excludeFloorPlan: props?.selectedExcludedFloorIds?.includes(item?.propertyId || "") || props?.createData?.excludeFloorPlan || false,
      excludePhotoGallery: props?.selectedExcludedPhotoGalleryIds?.includes(item?.propertyId || "") || false,
      excludeFurnishingDetails: props?.selectedExcludedFurnishingIds?.includes(item?.propertyId || "") || false,
      furnishingDetailOptionViews: props?.floorplanAndFurnishingDetails?.find((x: any) => x?.propertyId === item?.propertyId)?.furnishingDetailsViewOption || FurnishingDetailsViewOption.TwoColumn
    }));
  };

  const { data: proposedPropertyDataFromApi, loaded, loading } = useDataLoader<any>(
    () =>
      api.proposal.getProposalPreview({
        requirementId,
        propertyDetails: formatSelectedProperties(selectedProperties || []),
        temporaryData: props?.createData ?? undefined,
        proposalId: props?.proposalId || props?.proposalIdForPreview,
        projectImages: groupProjects(props?.selectedProjectImages || []),
      })
  );

  React.useEffect(() => {
    if (proposedPropertyDataFromApi && proposedPropertyDataFromApi?.temporaryData && createData) {
      setProposedPropertyData({ ...proposedPropertyDataFromApi, proposalDetails: { ...(proposedPropertyDataFromApi.proposalDetails || {}), ...(proposedPropertyDataFromApi.temporaryData || {}), capturedLocationImage: proposedPropertyDataFromApi?.temporaryData?.capturedLocationImage?.location } })
    } else {
      setProposedPropertyData(proposedPropertyDataFromApi)
    }
  }, [proposedPropertyDataFromApi])

  const {
    data: proposal,
    reload: reloadProposal,
    loaded: loadedProposal,
  } = useDataLoader<Proposal>(
    (proId: string) =>
      proId ? api.proposal.findProposalById(proId) : undefined,
    [proposalId]
  );

  let createData = props.createData;
  if (proposal) {
    createData = proposal;
  }

  const {
    performAction: downloadProposal,
    isPerforming: downloading,
  } = usePerformAction(
    async () => {
      const htmlContent = componentRef.current?.outerHTML;
      await api.proposal.downloadProposalPdf(
        requirementId,
        proposalId!,
        proposal.title,
        htmlContent,
      );
    },
    () => {
      toastr.success("Success", "Downloaded successfully");
      props.reload?.();
    },
    () => {
      toastr.error("Error", "Failed to download");
    }
  );

  const {
    performAction: sendProposalEmail,
    isPerforming: sending,
  } = usePerformAction(
    async () => {
      const htmlContent = componentRef.current?.outerHTML;
      await api.proposal.sendProposalEmail(requirementId, proposalId!, htmlContent);
    },
    () => {
      toastr.success("Success", "Proposal sent successfully");
      props.reload?.();
    },
    () => {
      toastr.error("Error", "Failed to sent");
    }
  );

  const updateProposedCommercials = async () => {
    await api.proposal.updateProposal({ proposalCommercialDetails: props.editedProposedPropertyData, updateCommercialDetails: true }, props?.proposalId || '');
    toastr.success("Success", "Proposal successfully updated");
    props?.refreshComponent?.()
  }

  const formatProperties = (properties: any) => {
    // we need to group the properties by project. and take the floor image of common properties and make array with it
    // also we need to make an array of furnishing details for each project
    // if commericialDetails is diferent between two properties of common project, then we need it as an another option

    return values(groupBy(properties, "projectId")) || [];
  };

  const getAmenityIcon = (amenityName: string): string => {
    // Map of amenity names to their corresponding icon paths
    const iconMap: { [key: string]: string } = {
      'Security': securityIcon,
      'Reserved Parking': parkingIcon,
      'Visitor Parking': visitorParkingIcon,
      'Centralized AC': acIcon,
      '24 hours water supply': waterSupplyIcon,
      'CCTV': cctvIcon
    };

    // Return the matching icon path or default icon if no match found
    return iconMap[amenityName] || defaultIcon;
  };

  const splitFloorOffered = (groupedProperties: {
    floorOffered: number;
    totalBuiltArea: number;
  }[]) => {
    const chunkSize = 20;
    const result = [];

    // If array is empty or less than chunk size, return as single chunk
    if (!groupedProperties?.length || groupedProperties.length <= chunkSize) {
      return [groupedProperties];
    }

    // Split array into chunks of 10
    for (let i = 0; i < groupedProperties.length; i += chunkSize) {
      const chunk = groupedProperties.slice(i, i + chunkSize);
      result.push(chunk);
    }

    return result;
  }

  function groupPropertiesByCommericialDetails(properties: PropertyDetails[] | undefined) {
    // Helper function to create a stable string key from commericialDetails array
    const getDetailsKey = (details: { attrName: string, attrOptionValue: string }[] | undefined) => {
      if (!details) return '';
      // Sort the details to ensure consistent comparison
      const sortedDetails = sortBy(details, ['attrName', 'attrOptionValue']);
      return JSON.stringify(sortedDetails);
    };

    // Create initial groups using commericialDetails as keys
    const groups = new Map();

    properties?.forEach(propertyData => {
      const detailsKey = getDetailsKey(propertyData?.commericialDetails);

      if (!groups.has(detailsKey)) {
        groups.set(detailsKey, {
          commericialDetails: propertyData.commericialDetails || [],
          propertyNames: [],
          properties: [],
        });
      }

      groups.get(detailsKey).propertyNames.push(`${propertyData.propertyName} - ${propertyData.floorOffered}`);
      groups.get(detailsKey).properties.push(propertyData);
    });

    // Convert Map to array of groups
    return Array.from(groups.values())
      // Filter out empty groups (if any properties had no commericialDetails)
      .filter(group => group.commericialDetails.length > 0)
      // Sort groups by number of properties for better organization
      .sort((a, b) => b.propertyNames.length - a.propertyNames.length);
  }

  if (!loaded || loading) {
    return (
      <div
        className={`detail-poupup  position-fixed top-0 w-100 h-100 overflow-auto in `}
        style={{ zIndex: 1051 }}
      >
      <ProposalPreviewHeader
        setStatusModalOpen={setStatusModalOpen}
        updating={updating}
        commercialsEdited={commercialsEdited}
        setCommercialsEdited={setCommercialsEdited}
        showYouWillLoseChangePopup={showYouWillLoseChangePopup}
        setShowYouWillLoseChangePopup={setShowYouWillLoseChangePopup}
        canCreateProposal={canCreateProposal}
        canUpdateProposal={canUpdateProposal}
        canApproveProposal={canApproveProposal}
        canRejectProposal={canRejectProposal}
        enableEdit={enableEdit}
        setEnableEdit={setEnableEdit}
        requirementData={requirementData}
        proposal={proposal}
        loadedProposal={loadedProposal}
        createData={createData}
        downloadProposal={downloadProposal}
        downloading={downloading}
        sendProposalEmail={sendProposalEmail}
        sending={sending}
        updateProposedCommercials={updateProposedCommercials}
        onClose={props.onClose}
        hideEdit={props.hideEdit}
        requirementId={props.requirementId}
        setEditedProposedPropertyData={props.setEditedProposedPropertyData}
        isEdited={props.isEdited}
        onEdit={props.onEdit}
        isSaving={props.isSaving}
        onSend={props.onSend}
        proposalIdForPreview={props.proposalIdForPreview}
        refreshComponent={props?.refreshComponent}
        hasAnyProposalApproved={props.requirementData?.hasAnyProposalApproved}
      ></ProposalPreviewHeader>
        <ContainerLoader></ContainerLoader>
      </div>
    );
  }

  return (
    <>
      <div
        className={`detail-poupup  position-fixed top-0 w-100 h-100 overflow-auto in `}
        style={{ zIndex: 1051 }}
      >
        <ProposalPreviewHeader
          setStatusModalOpen={setStatusModalOpen}
          updating={updating}
          commercialsEdited={commercialsEdited}
          setCommercialsEdited={setCommercialsEdited}
          showYouWillLoseChangePopup={showYouWillLoseChangePopup}
          setShowYouWillLoseChangePopup={setShowYouWillLoseChangePopup}
          canCreateProposal={canCreateProposal}
          canUpdateProposal={canUpdateProposal}
          canApproveProposal={canApproveProposal}
          canRejectProposal={canRejectProposal}
          enableEdit={enableEdit}
          setEnableEdit={setEnableEdit}
          requirementData={requirementData}
          proposal={proposal}
          loadedProposal={loadedProposal}
          createData={createData}
          downloadProposal={downloadProposal}
          downloading={downloading}
          sendProposalEmail={sendProposalEmail}
          sending={sending}
          updateProposedCommercials={updateProposedCommercials}
          onClose={props.onClose}
          hideEdit={props.hideEdit}
          requirementId={props.requirementId}
          setEditedProposedPropertyData={props.setEditedProposedPropertyData}
          isEdited={props.isEdited}
          onEdit={props.onEdit}
          isSaving={props.isSaving}
          onSend={props.onSend}
          proposalIdForPreview={props.proposalIdForPreview}
          refreshComponent={props?.refreshComponent}
          hasAnyProposalApproved={props.requirementData?.hasAnyProposalApproved}
        ></ProposalPreviewHeader>

        <div style={{ fontFamily: 'Nunito Sans, san-serif', maxWidth: '1320px', width: '100%' }} className="container-fluid px-4 bg-white min-vh-100 pt-2 pb-5 pdf-outer" ref={componentRef}>
          {/* new proposal  start*/}

          {/* images start  */}
          <ProposalImage
            proposedPropertyData={proposedPropertyData}
          />
          {/* images end  */}

          <ProposalProjectImages
            proposedPropertyDataFromApi={proposedPropertyDataFromApi}
          />

          {/* requirement breif start */}
            <ProposalRequirementBrief
              requirementData={requirementData}
            />  
          {/* requirement breif end */}

          {/* location map start  */}
              <ProposalLocationMap
                proposedPropertyData={proposedPropertyData}
              />
          {/* location map end */}

          <ProposalFormattedPropertiesDetails
            canEditCommercialDetails={canEditCommercialDetails}
            enableEdit={enableEdit}
            formatProperties={formatProperties}
            getAmenityIcon={getAmenityIcon}
            groupPropertiesByCommericialDetails={groupPropertiesByCommericialDetails}
            proposal={proposal}
            proposedPropertyData={proposedPropertyData}
            setIsEditProposedCommercials={setIsEditProposedCommercials}
            splitFloorOffered={splitFloorOffered}
          />

          {/* thankyou page start  */}
          <ProposalPreviewThankYou proposal={proposal}></ProposalPreviewThankYou>
          {/* thankyou page end */}

          {/* new proposal  end*/}
        </div>

        {statusModalOpen.isOpen &&
          statusModalOpen.status === SendProposalStatus.Approved ? (
          <ProposalApprovalModal setStatusModalOpen={setStatusModalOpen} updating={updating} changeStatus={changeStatus}></ProposalApprovalModal>
        ) : (
          <></>
        )}

        {statusModalOpen.isOpen &&
          statusModalOpen.status === SendProposalStatus.Rejected ? (
          <ProposalRejectModal setStatusModalOpen={setStatusModalOpen} reasons={reasons} control={control} reason={errors.reason} handleSubmit={handleSubmit} updating={updating} changeStatus={changeStatus}></ProposalRejectModal>
        ) : (
          <></>
        )}

        {isEditProposedCommercials.isOpen ?
          <EditProposedCommercialsWrapper setCommercialsEdited={setCommercialsEdited} setProposedPropertyData={setProposedPropertyData} isEditProposedCommercials={isEditProposedCommercials} setIsEditProposedCommercials={setIsEditProposedCommercials} setEditedProposedPropertyData={props.setEditedProposedPropertyData}></EditProposedCommercialsWrapper>
          : <></>}

        {showYouWillLoseChangePopup.isOpen ? (
          <ProposalYouWillLoseChangesPopup updating={updating} showYouWillLoseChangePopup={showYouWillLoseChangePopup} setShowYouWillLoseChangePopup={setShowYouWillLoseChangePopup}></ProposalYouWillLoseChangesPopup>
        ) : (
          <></>
        )}
      </div>
      <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>
    </>
  );
};

export default ProposalPreview;
